export const RESERVED_SHORTCODES = new Set([
  'admin',
  'api',
  'assets',
  'login',
  'logout',
  'favicon.ico',
  'robots.txt',
  'sitemap.xml',
  'static',
  'health',
  'dashboard',
  'auth',
  'public',
  'lnk',
  'preview',
  'setup-password',
  'invite',
  'index.html',
  'sw.js',
  'manifest.json',
  'undefined',
  'null',
]);

export interface ValidationResult {
  valid: boolean;
  normalizedCode: string;
  error?: string;
}

/**
 * Normalizes and validates a custom or generated shortcode
 */
export function validateShortcode(rawCode: string): ValidationResult {
  if (!rawCode || typeof rawCode !== 'string') {
    return {
      valid: false,
      normalizedCode: '',
      error: 'Shortcode is required and must be a string.',
    };
  }

  // 1. Trim whitespace & normalize to lowercase
  const normalized = rawCode.trim().toLowerCase();

  // 2. Length check: 2 to 50 characters
  if (normalized.length < 2 || normalized.length > 50) {
    return {
      valid: false,
      normalizedCode: normalized,
      error: 'Shortcode length must be between 2 and 50 characters.',
    };
  }

  // 3. Reserved words check (must happen before regex so dot-extensions like favicon.ico are caught)
  if (RESERVED_SHORTCODES.has(normalized)) {
    return {
      valid: false,
      normalizedCode: normalized,
      error: `The shortcode "${normalized}" is reserved for system use. Please choose another code.`,
    };
  }

  // 4. Leading / trailing hyphens check
  if (normalized.startsWith('-') || normalized.endsWith('-')) {
    return {
      valid: false,
      normalizedCode: normalized,
      error: 'Shortcode cannot begin or end with a hyphen.',
    };
  }

  // 5. Consecutive hyphens check (keep clean URLs)
  if (normalized.includes('--')) {
    return {
      valid: false,
      normalizedCode: normalized,
      error: 'Shortcode cannot contain consecutive hyphens.',
    };
  }

  // 6. Character set check: only lowercase alphanumeric and hyphens
  const validPattern = /^[a-z0-9-]+$/;
  if (!validPattern.test(normalized)) {
    return {
      valid: false,
      normalizedCode: normalized,
      error: 'Shortcode can only contain lowercase letters (a-z), numbers (0-9), and hyphens (-).',
    };
  }

  return {
    valid: true,
    normalizedCode: normalized,
  };
}

/**
 * Validates a destination URL.
 * Only allows HTTP and HTTPS schemes.
 * Rejects javascript:, data:, file:, etc.
 */
export function validateDestinationUrl(url: string, currentHost?: string): { valid: boolean; normalizedUrl: string; error?: string } {
  if (!url || typeof url !== 'string') {
    return { valid: false, normalizedUrl: '', error: 'Destination URL is required.' };
  }

  const trimmed = url.trim();

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { valid: false, normalizedUrl: '', error: 'Destination URL is not a valid absolute URL.' };
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { valid: false, normalizedUrl: '', error: 'Destination URL protocol must be HTTP or HTTPS.' };
  }

  // Prevent circular redirects to the short link service itself
  if (currentHost && parsed.host.toLowerCase() === currentHost.toLowerCase()) {
    // If the path looks like a short link on the same domain
    if (parsed.pathname && parsed.pathname.length > 1 && !parsed.pathname.startsWith('/admin')) {
      return {
        valid: false,
        normalizedUrl: '',
        error: `Destination URL cannot point to another short link on ${currentHost} to prevent circular redirects.`,
      };
    }
  }

  return { valid: true, normalizedUrl: parsed.toString() };
}

/**
 * Generates a cryptographically secure random shortcode.
 * Avoids visually ambiguous characters (0, O, 1, l, I).
 */
export function generateRandomShortcode(length = 6): string {
  const chars = '23456789abcdefghjkmnpqrstuvwxyz';
  const buffer = new Uint8Array(length);
  crypto.getRandomValues(buffer);

  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars[buffer[i] % chars.length];
  }
  return result;
}
